import { useEffect, useState } from "react";
import api from "../../api/axiosInstance";
import { AxiosError } from "axios";
import toast from "react-hot-toast";

type HTTPMethod = "get" | "post" | "put" | "patch" | "delete";

interface ApiMeta {
  [key: string]: any;
}

interface FetchOverride<Request = any> {
  method?: HTTPMethod;
  body?: Request;
  params?: Record<string, any>;
  url?: string;
}

type ToastMessages = {
  loading?: string;
  success?: string;
  error?: string;
};

export interface UseFetchOptions<Request = any> {
  initialData?: any;
  params?: Record<string, any>;
  skipInitialFetch?: boolean;
  method?: HTTPMethod;
  body?: Request; // SEND type
  autoFetch?: boolean;
  withToast?: boolean | ToastMessages;
}

export interface UseFetchResult<Response, Request = any> {
  data: Response;
  meta: ApiMeta | null;
  loading: boolean;
  error: AxiosError | null;
  success: boolean;
  fetchData: (override?: FetchOverride<Request>) => Promise<Response | null>;
  refetch: (override?: FetchOverride<Request>) => Promise<Response | null>;
  setData: React.Dispatch<React.SetStateAction<Response>>;
}

const useFetch = <Response = unknown, Request = any>(
  url: string,
  options: UseFetchOptions<Request> = {}
): UseFetchResult<Response, Request> => {
  const {
    initialData = null,
    params = {},
    method = "get",
    body = null,
    skipInitialFetch = false,
    autoFetch = !skipInitialFetch,
    withToast,
  } = options;

  const [data, setData] = useState<Response>(initialData as Response);
  const [meta, setMeta] = useState<ApiMeta | null>(null);
  const [loading, setLoading] = useState<boolean>(!!autoFetch);
  const [error, setError] = useState<AxiosError | null>(null);
  const [success, setSuccess] = useState<boolean>(false);

  const toastConfig: ToastMessages =
    typeof withToast === "object"
      ? withToast
      : withToast
        ? {
            loading: "Processing...",
            success: "Done!",
            error: "Something went wrong",
          }
        : {};

  const fetchData = async (
    override: FetchOverride<Request> = {}
  ): Promise<Response | null> => {
    const fetchMethod = override.method || method || "get";
    const fetchParams = override.params ?? params;
    const fetchBody = override.body !== undefined ? override.body : body;
    const requestUrl = override.url || url;

    setLoading(true);
    setSuccess(false);
    setError(null);

    if (withToast && toastConfig.loading) toast.loading(toastConfig.loading);

    try {
      const response = await api.request({
        url: requestUrl,
        method: fetchMethod,
        params: fetchParams,
        data: fetchBody,
      });

      const resData = response.data?.data ?? null;

      if ([200, 201, 204].includes(response.status)) {
        if (fetchMethod === "get") {
          setData(resData);
          setMeta(response.data?.meta || null);
        }

        setSuccess(true);

        if (withToast && toastConfig.success) {
          toast.dismiss();
          toast.success(toastConfig.success);
        }

        return resData;
      } else {
        const err = new AxiosError(
          `Request failed, status code: ${response.status}`
        );
        setError(err);
        setSuccess(false);

        if (withToast && toastConfig.error) {
          toast.dismiss();
          toast.error(toastConfig.error);
        }

        return null;
      }
    } catch (err: any) {
      setError(err || "Request error");
      setSuccess(false);

      if (withToast) {
        toast.dismiss();
        toast.error(
          err?.response?.data?.message ||
            toastConfig.error ||
            "Something went wrong"
        );
      }

      return null;
    } finally {
      setLoading(false);
    }
  };

  const refetch = (override?: FetchOverride<Request>) => fetchData(override);

  useEffect(() => {
    if (autoFetch) {
      fetchData();
    }
  }, [url, JSON.stringify(params)]);

  return {
    data,
    meta,
    loading,
    error,
    success,
    fetchData,
    refetch,
    setData,
  };
};

export default useFetch;
